import { getERPClient } from "@/lib/erp/client";
import { useAuthStore } from "@/lib/store/authStore";
import type {
  MarketplaceConnection,
  MarketplaceSyncStatus,
  SyncLogResponse,
  ChannelSummary,
  TriggerSyncResponse,
  TriggerSyncAllResponse,
  InitiateOAuthResponse,
  PushAllChannelResponse,
  ConnectionSettings,
} from "./marketplaceTypes";

const API = "zaviago_backend.storefront.marketplace";

/** Trusted OAuth redirect domains */
const TRUSTED_OAUTH_DOMAINS = [
  "lazada.com",
  "auth.lazada.com",
  "tiktokshop.com",
  "services.tiktokshop.com",
  "shopeemobile.com",
];

function getCompany(): string {
  const company = useAuthStore.getState().tenantCompany;
  if (!company) throw new Error("No tenant company selected");
  return company;
}

/** Validate that an OAuth redirect URL is on a trusted domain */
function validateRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return TRUSTED_OAUTH_DOMAINS.some(
      (d) => parsed.hostname === d || parsed.hostname.endsWith(`.${d}`),
    );
  } catch {
    return false;
  }
}

/**
 * Map lowercase URL channel param to title-case API channel.
 * Backend stores "Lazada" not "lazada".
 */
export function toApiChannel(urlChannel: string): string {
  const map: Record<string, string> = {
    lazada: "Lazada",
    shopee: "Shopee",
    tiktok: "TikTok Shop",
  };
  return map[urlChannel.toLowerCase()] || urlChannel;
}

export const marketplaceApi = {
  getConnections() {
    return getERPClient().call<MarketplaceConnection[]>(
      `${API}.get_connections`,
      { company: getCompany() },
    );
  },

  getSyncStatus(channel: string, connectionName?: string) {
    return getERPClient().call<MarketplaceSyncStatus>(
      `${API}.get_sync_status`,
      { channel, company: getCompany(), ...(connectionName && { connection_name: connectionName }) },
    );
  },

  getSyncLog(channel: string, page?: number, pageSize?: number, connectionName?: string) {
    return getERPClient().call<SyncLogResponse>(
      `${API}.get_sync_log`,
      { channel, company: getCompany(), page, page_size: pageSize, ...(connectionName && { connection_name: connectionName }) },
    );
  },

  getChannelSummary(channel: string, connectionName?: string, period?: string) {
    return getERPClient().call<ChannelSummary>(
      `${API}.get_channel_summary`,
      { channel, company: getCompany(), ...(connectionName && { connection_name: connectionName }), ...(period && { period }) },
    );
  },

  triggerSync(channel: string, syncType: string, connectionName?: string) {
    return getERPClient().call<TriggerSyncResponse>(
      `${API}.trigger_sync`,
      { channel, sync_type: syncType, company: getCompany(), ...(connectionName && { connection_name: connectionName }) },
    );
  },

  triggerSyncAll(channel: string) {
    return getERPClient().call<TriggerSyncAllResponse>(
      `${API}.trigger_sync_all`,
      { channel, company: getCompany() },
    );
  },

  disconnect(channel: string, connectionName?: string) {
    return getERPClient().call<{ success: boolean }>(
      `${API}.disconnect`,
      { channel, company: getCompany(), ...(connectionName && { connection_name: connectionName }) },
    );
  },

  async initiateOAuth(channel: string, connectionName?: string) {
    const result = await getERPClient().call<InitiateOAuthResponse>(
      `${API}.initiate_oauth`,
      { channel, company: getCompany(), ...(connectionName && { connection_name: connectionName }) },
    );
    if (!validateRedirectUrl(result.redirect_url)) {
      throw new Error("Untrusted redirect URL received from server");
    }
    return result;
  },

  pushAllChannel(channel: string, connectionName: string) {
    return getERPClient().call<PushAllChannelResponse>(
      `${API}.push_all_channel`,
      { channel, connection_name: connectionName, company: getCompany() },
    );
  },

  updateConnectionSettings(connectionName: string, settings: ConnectionSettings) {
    return getERPClient().call<{ success: boolean }>(
      `${API}.update_connection_settings`,
      { connection_name: connectionName, company: getCompany(), ...settings },
    );
  },
};
